import type { ShopUser } from '@scayle/storefront-nuxt'
import { getEmailHash, mapCustomerInfoToTrackingPayload } from '~/utils'
import type {
  TrackingEvent,
  TrackingPayload,
  AuthenticationType,
  AuthTrackingEvent,
  CustomerType,
  CustomerData,
} from '~/types/tracking'
import { useUser } from '#storefront/composables'

export type AuthTrackingEventData = {
  category?: string
  page_type?: string
  customer_id?: number
  customer_type?: CustomerType
  eh?: string
  event: AuthTrackingEvent
  method?: AuthenticationType
  status?: 'successful' | 'error'
  intent?: string
  mur_id?: string
  login?: ShopUser | undefined
}

function getAgeFromBirthDate(birthDate: ShopUser['birthDate']) {
  let _age: number | undefined
  if (!birthDate) {
    return undefined
  }
  const _birthDate = new Date(birthDate)
  const today = new Date()
  _age = today.getFullYear() - _birthDate.getFullYear()
  const monthDiff = today.getMonth() - _birthDate.getMonth()
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < _birthDate.getDate())
  ) {
    _age--
  }
  return _age || undefined
}

const useUserActionEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => void,
) => {
  const METHOD_DEFAULT: AuthenticationType = 'email'

  return {
    trackLogout: () => {
      track('logout', {
        customer_id: undefined,
        eh: undefined,
      })
    },

    trackAuthenticated: async (payload: AuthTrackingEventData, email = '') => {
      const eh = payload.eh || (email && (await getEmailHash(email)))
      const { customer_id: customerId, method, status, event } = payload

      const { user } = await useUser()

      let additionalCustomerData = {}
      if (status === 'successful') {
        additionalCustomerData = {
          category: 'account',
          action: 'login',
          label: `${method || METHOD_DEFAULT} ${status}`,
          logged_in: METHOD_DEFAULT,
          login_status: true,
          gender: user.value?.gender || undefined,
          age: getAgeFromBirthDate(user.value?.birthDate),
          mur_id: user.value?.referenceKey,
        }
      }

      track(
        event || 'login',
        mapCustomerInfoToTrackingPayload(
          {
            customer_id: customerId,
            method: METHOD_DEFAULT,
            eh: eh || '0',
            status,
            pagePayload: {
              ...additionalCustomerData,
            },
          },
          true,
        ),
      )
    },

    trackSignUp: async (payload: AuthTrackingEventData, email = '') => {
      const eh = payload.eh || (email && (await getEmailHash(email)))
      const { status, intent, login, customer_id, mur_id } = payload

      track('sign_up', {
        method: METHOD_DEFAULT,
        eh,
        status,
        category: 'account',
        mur_id,
        action: 'sign_up',
        label: `${METHOD_DEFAULT} ${status}`,
        intent,
        customer_id,
        Login: {
          logged_in: login?.authentication?.type || '',
          login_status: !!login,
          gender: login?.gender || '',
        },
      } as CustomerData)
    },

    trackSwitchShop: (payload: { action: string; label: string }) => {
      track('change_shop', {
        category: 'shop_selection',
        ...payload,
      })
    },
  }
}

export default useUserActionEvents
